import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { submitAnswer } from '@db/submissionService';
import { getCurrentUid, ensureAnonymousAuth } from '@db/firebase';
import { subscribeToRoom } from '@db/roomService';
import type { SlideDrawingConfig } from '@types-local/quiz.types';
import type { Room } from '@types-local/room.types';

export default function AnswerDraw(): React.ReactElement {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawing = useRef(false);
  const [room, setRoom] = useState<Room | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const studentName = sessionStorage.getItem('gh_student_name') ?? 'Student';
  const roomCode = sessionStorage.getItem('gh_room_code') ?? '';
  const classId = sessionStorage.getItem('gh_class_id') ?? '';

  useEffect(() => {
    ensureAnonymousAuth();
    if (!roomCode) return;
    return subscribeToRoom(roomCode, setRoom);
  }, [roomCode]);

  const config = room?.activeConfig?.type === 'slide_drawing'
    ? (room.activeConfig as SlideDrawingConfig) : null;

  // Reset for each new activity so students flow through the session.
  useEffect(() => {
    setSubmitted(false);
    const ctx = canvasRef.current?.getContext('2d');
    if (ctx && canvasRef.current) {
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    }
  }, [config?.prompt]);

  const getCanvasPos = (e: React.PointerEvent) => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) * (canvas.width / rect.width),
      y: (e.clientY - rect.top) * (canvas.height / rect.height),
    };
  };

  const startDraw = (e: React.PointerEvent) => {
    isDrawing.current = true;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const { x, y } = getCanvasPos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.strokeStyle = '#1a1a2e';
    ctx.lineWidth = 4;
    ctx.lineCap = 'round';
  };

  const draw = (e: React.PointerEvent) => {
    if (!isDrawing.current) return;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const { x, y } = getCanvasPos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const handleSubmit = async () => {
    if (!canvasRef.current || !room?.activeSlideId) return;
    setSubmitting(true);
    try {
      const dataUrl = canvasRef.current.toDataURL('image/png');
      const uid = getCurrentUid() ?? 'anon';
      await submitAnswer(roomCode, uid, studentName, room.activeSlideId, {
        type: 'slide_drawing',
        imageDataUrl: dataUrl,
      }, classId);
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  if (!config) {
    return (
      <div className="min-h-screen flex items-center justify-center text-center p-6">
        <div className="space-y-3">
          <div className="text-5xl">🎨</div>
          <h2 className="text-xl font-bold text-slate-900">Waiting for drawing activity…</h2>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center text-center p-6">
        <div className="space-y-4">
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="text-6xl">🎉</motion.div>
          <h2 className="text-xl font-bold text-slate-900">Drawing Submitted!</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col p-4">
      <h2 className="text-base font-bold text-slate-900 mb-3">{config.prompt}</h2>
      <canvas
        ref={canvasRef}
        width={600}
        height={400}
        className="w-full rounded-2xl bg-white border-2 border-surface-border touch-none"
        style={{ cursor: 'crosshair' }}
        onPointerDown={startDraw}
        onPointerMove={draw}
        onPointerUp={() => { isDrawing.current = false; }}
      />
      <motion.button
        onClick={handleSubmit}
        disabled={submitting}
        whileTap={{ scale: 0.97 }}
        className="mt-4 w-full py-4 rounded-2xl bg-gradient-to-r from-purple-600 to-brand-500 text-white font-bold text-base disabled:opacity-60"
      >
        {submitting ? 'Submitting…' : '🚀 Submit Drawing'}
      </motion.button>
    </div>
  );
}
